import { Container, Dropdown, OutsideClickHandler, Typography } from '@cleartrip/ct-design-components'
import { useState } from 'react'

import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { useAppSelector } from '@/redux/store/hooks'

import { ravenSDKSrpCfwTrigger } from '@/analytics/raven/ravenSDKHelper'
import { DownChevronNew } from '@/assets/icons/DownChevron'
import { srpHostedIcon } from '@/assets/icons/SrpHostedIcon'
import useWowPricingExperiment from '@/hooks/srp/useWowPricingExperiment'
import { SortOptionType } from '@/types/srp'
import { isEmpty } from '@/utils/general/browserHelper'
import { ProcessedData } from '@/utils/srp/types'

import SortbarLoader from '../Loaders/SortBarLoader'

type DropdownOption = {
  key: string
  label: string
  sort: SortOptionType
  raven: Record<string, string | number>
}

type SortBarOneWayProps = {
  activeSort: SortOptionType[]
  setActiveSort: (activeSort: SortOptionType[] | ((prevState: SortOptionType[]) => SortOptionType[])) => void
  J1from: string
  cheapestFilteredFlight: string
  fastestFilteredFlight?: string
  isLoading: boolean
  processedData: Partial<ProcessedData>
  initialTS?: number
}

/** Three equal chips across the 912px results column: 296 * 3 + 12 * 2. */
const CheapestIcon = srpHostedIcon('currency.svg')
const FastestIcon = srpHostedIcon('share.svg')
const SortIcon = srpHostedIcon('leading.svg')

const CHIP = { height: '56px', padding: '8px 12px', boxSizing: 'border-box' } as const
const CHIP_SELECTED = { ...CHIP, boxShadow: 'inset 0 0 0 1px #1A1A1A' } as const
const CHIP_BASE_CLASS = 'flex flex-1 flex-middle flex-cg-2 ws-nowrap c-pointer br-12 ba-solid bg-neutral-100'
const CHIP_CLASS = `${CHIP_BASE_CLASS} bc-line-500`
const CHIP_CLASS_SELECTED = `${CHIP_BASE_CLASS} bc-neutral-900`
/** `top` is explicit — an absolute child of a flex row otherwise sits at the line's start, overlapping the chip. */
const DROPDOWN = { minWidth: '296px', zIndex: 30, top: 'calc(100% + 8px)' } as const

const SORT_PLACEHOLDER = 'By time/duration etc'

/** The hook builds these with a middot; the design separates with a pipe. */
const withPipe = (label: string) => label.replace(/\s*·\s*/, ' | ')

const SortBarOneWay = ({
  J1from,
  activeSort,
  cheapestFilteredFlight,
  fastestFilteredFlight,
  initialTS,
  isLoading,
  processedData,
  setActiveSort,
}: SortBarOneWayProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { searchData } = useAppSelector(getSrpSearchData)
  const { wowOn } = useWowPricingExperiment(!!processedData?.isIntl)
  // Matches the legacy bar: WOW sort only on CFW or international one-way.
  const showWowPriceSort = wowOn && (!!processedData?.isCfw || !!processedData?.isIntl)
  const [{ sortType, sortingBasis }] = activeSort

  const isCheapest = sortType === 'asc' && sortingBasis === 'price'
  const isFastest = sortType === 'asc' && sortingBasis === 'duration'

  /** Everything that is not one of the two chips lives in the dropdown. */
  const dropdownOptions: DropdownOption[] = [
    {
      key: 'departure_up',
      label: `Early Departure (${J1from})`,
      sort: { title: `Early Departure (${J1from})`, sortType: 'asc', sortingBasis: 'depart', sortKey: 'departure_up' },
      raven: {
        filter_type: 'departure_sort',
        filter_value: `Early Departure (${J1from})`,
        a_is_sort_applied: 'yes',
        a_quick_sort: 'no',
        a_nearby_flights: '0',
      },
    },
    {
      key: 'departure_down',
      label: `Late Departure (${J1from})`,
      sort: {
        title: `Late Departure (${J1from})`,
        sortType: 'desc',
        sortingBasis: 'depart',
        sortKey: 'departure_down',
      },
      raven: {
        filter_type: 'departure_sort',
        filter_value: `Late Departure (${J1from})`,
        a_is_sort_applied: 'yes',
        a_quick_sort: 'no',
        a_nearby_flights: '0',
      },
    },
  ]

  if (showWowPriceSort) {
    dropdownOptions.push({
      key: 'wow_price_up',
      label: 'Wow price low to high',
      sort: {
        title: 'Wow price low to high',
        sortType: 'asc',
        sortingBasis: 'wow_price',
        sortKey: 'wow_price_up',
      },
      // Payload kept exactly as the legacy quick-sort card sent it.
      raven: { filter_value: '', filter_type: 'WOW price - low to high' },
    })
  }

  const selectedDropdownOption = dropdownOptions.find(option => option.sort.sortKey === activeSort?.[0]?.sortKey)

  const applySort = (sort: SortOptionType, raven: Record<string, string | number>) => {
    setActiveSort([sort, sort])
    ravenSDKSrpCfwTrigger('a_sort_applied', searchData, processedData, raven)
  }

  const toggleDropdown = () => {
    if (!isDropdownOpen && searchData && processedData) {
      ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, {
        action_name: 'sort_results_tab_click',
        action_type: 'click',
        property: 'onward',
      })
    }
    setIsDropdownOpen(open => !open)
  }

  if (isLoading) {
    return <SortbarLoader />
  }

  const chip = (
    icon: React.ReactNode,
    title: string,
    subTitle: string,
    isSelected: boolean,
    onClick: () => void,
    trailing?: React.ReactNode
  ) => (
    <Container
      className={isSelected ? CHIP_CLASS_SELECTED : CHIP_CLASS}
      css={isSelected ? CHIP_SELECTED : CHIP}
      onClick={onClick}
    >
      {icon}
      <Container className='flex flex-1 flex-column o-hidden'>
        <Typography className='c-pointer' variant='B2'>
          {title}
        </Typography>
        <Typography className='c-pointer t-truncate' color='subheading' variant='P3'>
          {subTitle}
        </Typography>
      </Container>
      {trailing}
    </Container>
  )

  return (
    <Container className='flex flex-cg-3 mt-4'>
      {chip(<CheapestIcon />, 'Cheapest', withPipe(cheapestFilteredFlight), isCheapest, () => {
        if (isCheapest) return
        applySort({ title: 'Cheapest', sortType: 'asc', sortingBasis: 'price', sortKey: 'price_up' }, {
          filter_value: '',
          filter_type: 'Cheapest',
        })
      })}

      {chip(<FastestIcon />, 'Fastest', withPipe(fastestFilteredFlight || ''), isFastest, () => {
        if (isFastest) return
        applySort({ title: 'Fastest', sortType: 'asc', sortingBasis: 'duration', sortKey: 'duration_up' }, {
          filter_value: '',
          filter_type: 'Fastest',
        })
      })}

      <Container className='flex flex-1 p-relative'>
        {chip(
          <SortIcon />,
          'Sort',
          selectedDropdownOption?.label || SORT_PLACEHOLDER,
          !isEmpty(selectedDropdownOption),
          toggleDropdown,
          <DownChevronNew style={{ transform: isDropdownOpen ? 'rotate(-180deg)' : 'rotate(0deg)' }} />
        )}

        {isDropdownOpen && (
          <OutsideClickHandler onOutsideClick={() => setIsDropdownOpen(false)}>
            <Container className='p-absolute l-0' css={DROPDOWN}>
              <Dropdown
                optionList={dropdownOptions.map(({ key, label }) => ({ key, label }))}
                /* The chip already reflects the choice; the list stays unhighlighted. */
                selectedOption={[]}
                styleConfig={{
                  listDropdown: {
                    labelContainer: { className: 'c-pointer' },
                    labelTypography: { className: 'c-pointer' },
                  },
                }}
                onClick={(key: string) => {
                  const option = dropdownOptions.find(dropdownOption => dropdownOption.key === key)
                  if (option) {
                    applySort(option.sort, {
                      ...option.raven,
                      page_loadtime: initialTS ? new Date().getTime() - initialTS : 'N/A',
                    })
                  }
                  setIsDropdownOpen(false)
                }}
              />
            </Container>
          </OutsideClickHandler>
        )}
      </Container>
    </Container>
  )
}

export default SortBarOneWay
